import React, { useState, useEffect, useRef } from 'react';
import { Modal, Button } from 'antd';
import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import { Player } from '@lottiefiles/react-lottie-player';
import axios from '../../config/axiosInstance';

//import waiting from '../../assets/animations/waiting.json';
import success from '../../assets/animations/success.json';
import printing from '../../assets/animations/printing.json';
import waiting from '../../assets/animations/scan_qrcode.json';
import denied from '../../assets/animations/denied.json';
//import checking from '../../assets/animations/checking.json';
import gear from '../../assets/animations/gear.json';
import { fontFamilyToCss } from './helpers/textFonts';
import { useAuth } from '../../context/AuthContext';

const DeviceConfigPreview = ({ open, onCancel, deviceConfig }) => {
    const { hasFeature } = useAuth?.() || { hasFeature: () => false };

    // The design (fullscreen background, text style) is only active if the config's
    // client has the "deviceConfig.design" feature - same rule as on the devices.
    // In the admin list clientID is populated with its features, otherwise the
    // features of the logged-in user's own client apply.
    const clientFeatures = deviceConfig?.clientID?.features;
    const hasDesign = Array.isArray(clientFeatures)
        ? clientFeatures.includes('deviceConfig.design')
        : hasFeature('deviceConfig.design');
    const borderSize = 20;
    const minWidth = 1200;
    const aspectRatio = 1080 / 1920;
    const deviceWidth = minWidth;
    const deviceHeight = deviceWidth * aspectRatio;
    // Scale factor between the real device resolution (1920px wide) and the simulated device
    const deviceScale = deviceWidth / 1920;

    // Client default images for the per-phase fallback (fetched below together
    // with the default animations when clientID is not populated)
    const [ownImageDefaults, setOwnImageDefaults] = useState({});

    // Animation behaviour (design feature); without the feature the devices fall back
    // to the standard behaviour (animations on, small inline size, built-in defaults).
    // The size is a percentage of the screen; 100% means fullscreen.
    const globalAnimationsEnabled = hasDesign ? deviceConfig?.IsLottiesAnimationsEnabled !== false : true;
    // Every phase additionally has its own animation switch
    const phaseAnimationEnabled = (phaseKey) =>
        hasDesign ? deviceConfig?.PhaseAnimationsEnabled?.[phaseKey] !== false : true;
    const animationSizePercent = hasDesign && deviceConfig?.AnimationSize > 0 ? deviceConfig.AnimationSize : 25;
    const animationFullScreen = !!(hasDesign
        && (deviceConfig?.IsAnimationFullScreenEnabled || animationSizePercent >= 100));
    const isImageAnimation = (url) => /\.(png|gif)(\?.*)?$/i.test(url || '');

    // Client defaults for the animation fallback: in the admin list clientID is
    // populated with its defaultAnimations, otherwise the own client's are fetched.
    const [ownDefaults, setOwnDefaults] = useState({});

    useEffect(() => {
        if (!open || !hasDesign) return;
        if (deviceConfig?.clientID && typeof deviceConfig.clientID === 'object') return;

        axios.get('/clients/design/default-animations')
            .then(({ data }) => setOwnDefaults(data.defaultAnimations || {}))
            .catch(() => setOwnDefaults({}));
        axios.get('/clients/design/default-images')
            .then(({ data }) => setOwnImageDefaults(data.defaultImages || {}))
            .catch(() => setOwnImageDefaults({}));
    }, [open, hasDesign, deviceConfig]);

    const clientImageDefaults = hasDesign
        ? ((deviceConfig?.clientID && typeof deviceConfig.clientID === 'object'
            ? deviceConfig.clientID.defaultImages
            : ownImageDefaults) || {})
        : {};

    // Banner/background of a phase, same resolution as the delivery to the devices
    const phaseImageFor = (phaseKey) => {
        if (!hasDesign) {
            return { useBackground: false, banner: deviceConfig?.BannerUrl || '', background: '' };
        }

        const phase = deviceConfig?.PhaseImages?.[phaseKey];
        if (!phase || phase.useDefault !== false) {
            // Strictly the client-wide default images, same as the delivery gate
            return {
                useBackground: clientImageDefaults?.isBackgroundImageEnabled === true,
                banner: clientImageDefaults?.bannerUrl || '',
                background: clientImageDefaults?.backgroundUrl || '',
            };
        }

        return {
            useBackground: phase.isBackgroundImageEnabled === true,
            banner: phase.bannerUrl || '',
            background: phase.backgroundUrl || '',
        };
    };

    const clientDefaults = hasDesign
        ? ((deviceConfig?.clientID && typeof deviceConfig.clientID === 'object'
            ? deviceConfig.clientID.defaultAnimations
            : ownDefaults) || {})
        : {};

    // Same fallback chain as the delivery to the devices
    const effectiveAnimation = (configUrl, phaseKey, builtin) =>
        hasDesign ? (configUrl || clientDefaults[phaseKey] || builtin) : builtin;

    const [bannerHeight, setBannerHeight] = useState(150);
    // Animation size as percentage of the simulated device height
    const scaledLottieSize = (deviceHeight * animationSizePercent) / 100;
    const [currentStep, setCurrentStep] = useState(0);
    const [steps, setSteps] = useState([]);

    const deviceRef = useRef(null);

    useEffect(() => {
        if (deviceConfig) {
            // Animation per phase, same fallback chain as the delivery to the devices:
            // config URL -> client default -> built-in default
            setSteps([
                { key: 'TextWaiting', phaseKey: 'waiting', label: 'Waiting', text: deviceConfig.TextWaiting, animation: effectiveAnimation(deviceConfig.LottieWaitingUrl, 'waiting', waiting) },
                { key: 'TextCheckin', phaseKey: 'checking', label: 'Checking', text: deviceConfig.TextCheckin, animation: effectiveAnimation(deviceConfig.LottieCheckingUrl, 'checking', gear) },
                { key: 'TextPrinting', phaseKey: 'printing', label: 'Printing', text: deviceConfig.TextPrinting, animation: effectiveAnimation(deviceConfig.LottiePrintingUrl, 'printing', printing) },
                { key: 'TextCreating', phaseKey: 'success', label: 'Creating', text: deviceConfig.TextCreating, animation: effectiveAnimation(deviceConfig.LottieSuccessUrl, 'success', success) },
                { key: 'TextDenying', phaseKey: 'denied', label: 'Denying', text: deviceConfig.TextDenying, animation: effectiveAnimation(deviceConfig.LottieDeniedUrl, 'denied', denied) },
                { key: 'TextBlacklist', phaseKey: 'denied', label: 'Blacklist', text: deviceConfig.TextBlacklist, animation: effectiveAnimation(deviceConfig.LottieDeniedUrl, 'denied', denied) },
                { key: 'TextEFNError', phaseKey: 'denied', label: 'EFN Error', text: deviceConfig.TextEFNError, animation: effectiveAnimation(deviceConfig.LottieDeniedUrl, 'denied', denied) },
            ]);
        }
    }, [deviceConfig, hasDesign, JSON.stringify(clientDefaults)]);

    const handleImageLoad = (e) => {
        const { naturalWidth, naturalHeight } = e.target;
        if (naturalWidth && naturalHeight) {
            const ratio = naturalHeight / naturalWidth;
            const calculatedHeight = deviceWidth * ratio;
            setBannerHeight(calculatedHeight);
        }
    };

    const handlePrev = () => {
        if (currentStep > 0) setCurrentStep(currentStep - 1);
    };

    const handleNext = () => {
        if (currentStep < steps.length - 1) setCurrentStep(currentStep + 1);
    };

    useEffect(() => {
        if (open) {
            setCurrentStep(0);
        }
    }, [open]);

    return (
        <Modal
            title={null}
            open={open}
            onCancel={onCancel}
            footer={null}
            destroyOnClose
            width={deviceWidth + borderSize * 2}
            style={{ top: 20, padding: 0 }}
            bodyStyle={{
                padding: 0,
                margin: 0,
                borderRadius: '10px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                position: 'relative',
                backgroundColor: 'transparent',
            }}
        >
            {/* Simulated device container; banner/background follow the current phase */}
            {(() => {
                const phaseImage = phaseImageFor(steps[currentStep]?.phaseKey || 'waiting');
                const useBackground = !!(phaseImage.useBackground && phaseImage.background);
                const animationsEnabled = globalAnimationsEnabled
                    && phaseAnimationEnabled(steps[currentStep]?.phaseKey || 'waiting');
                return (
            <div
                ref={deviceRef}
                style={{
                    width: `${deviceWidth}px`,
                    height: `${deviceHeight}px`,
                    backgroundColor: '#ffffff',
                    border: `${borderSize}px solid black`,
                    borderRadius: '20px',
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                    boxSizing: 'border-box',
                    margin: '0 auto',
                    position: 'relative',
                    ...(useBackground && {
                        backgroundImage: `url(${phaseImage.background})`,
                        backgroundSize: 'cover',
                        backgroundRepeat: 'no-repeat',
                        backgroundPosition: 'center',
                    }),
                }}
            >
                {/* Banner (hidden when the fullscreen background is used) */}
                {!useBackground && phaseImage.banner && (
                    <div
                        style={{
                            width: '100%',
                            height: `${bannerHeight}px`,
                            backgroundImage: `url(${phaseImage.banner})`,
                            backgroundSize: 'contain',
                            backgroundRepeat: 'no-repeat',
                            backgroundPosition: 'center',
                            transition: 'height 0.3s ease',
                        }}
                    >
                        <img
                            src={phaseImage.banner}
                            alt="Banner"
                            style={{ display: 'none' }}
                            onLoad={handleImageLoad}
                        />
                    </div>
                )}

                {/* Content */}
                <div
                    style={{
                        flex: 1,
                        padding: '20px',
                        textAlign: 'center',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                    }}
                >
                    {/* Mirrors the app label: bold, wrapped at 800 device units, centered above the animation.
                        <br/> becomes a line break like on the device (pre-line renders the \n);
                        the ${...} placeholders stay unresolved, the preview has no participant. */}
                    <h2 style={{
                        color: (hasDesign && deviceConfig?.TextColor) || 'black',
                        marginBottom: `${20 * deviceScale}px`,
                        fontSize: hasDesign && deviceConfig?.TextFontSize > 0
                            ? `${deviceConfig.TextFontSize * deviceScale}px`
                            : `${60 * deviceScale}px`,
                        fontFamily: fontFamilyToCss(hasDesign ? deviceConfig?.TextFontFamily : ''),
                        fontWeight: 'bold',
                        maxWidth: `${800 * deviceScale}px`,
                        textAlign: 'center',
                        lineHeight: '1.2',
                        whiteSpace: 'pre-line'
                    }}>
                        {(steps[currentStep]?.text || '').replace(/<br\s*\/?>/gi, '\n')}
                    </h2>
                    {/* Phase animation: hidden when disabled, PNG/GIF as image, otherwise Lottie.
                        Fullscreen mode covers the whole simulated device. */}
                    {animationsEnabled && !animationFullScreen && (
                        isImageAnimation(steps[currentStep]?.animation) ? (
                            <img
                                src={steps[currentStep]?.animation}
                                alt="animation"
                                style={{ height: `${scaledLottieSize}px`, width: `${scaledLottieSize}px`, objectFit: 'contain' }}
                            />
                        ) : (
                            <Player
                                autoplay
                                loop
                                src={steps[currentStep]?.animation}
                                style={{ height: `${scaledLottieSize}px`, width: `${scaledLottieSize}px` }}
                            />
                        )
                    )}
                </div>

                {animationsEnabled && animationFullScreen && (
                    <div
                        style={{
                            position: 'absolute',
                            inset: 0,
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            pointerEvents: 'none',
                        }}
                    >
                        {isImageAnimation(steps[currentStep]?.animation) ? (
                            <img
                                src={steps[currentStep]?.animation}
                                alt="animation"
                                style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                            />
                        ) : (
                            <Player
                                autoplay
                                loop
                                src={steps[currentStep]?.animation}
                                style={{ height: '100%', width: '100%' }}
                            />
                        )}
                    </div>
                )}
            </div>
                );
            })()}

            {/* Navigation controls */}
            <div
                style={{
                    marginTop: '20px',
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: '12px',
                }}
            >
                {currentStep > 0 && (
                    <Button type="primary" icon={<LeftOutlined />} onClick={handlePrev} />
                )}

                <span style={{ fontWeight: 'bold', fontSize: '16px' }}>
                    {steps[currentStep]?.label || deviceConfig?.DeviceConfigName || 'Device Config'}
                </span>

                {currentStep < steps.length - 1 && (
                    <Button type="primary" icon={<RightOutlined />} onClick={handleNext} />
                )}
            </div>
        </Modal>
    );
};

export default DeviceConfigPreview;